// Add / edit beneficiary (§5): alias, account blocks, review, OTP approval,
// unsaved-changes guard and the remove-last-account delete flow. The save is
// one atomic call (§17.9) and the update toast uses the NEW alias (§17.2).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  createBeneficiary,
  deleteBeneficiary,
  listBeneficiaries,
  requestBeneficiaryOtp,
  resendBeneficiaryOtp,
  updateBeneficiary,
  verifyBeneficiaryOtp,
} from '@/api/beneficiaries';
import { getCountries } from '@/api/reference';
import { ApiError } from '@/api/client';
import { MOCK_BENEFICIARIES } from '@/api/flags';
import { getTestPanelState, setSkipCooling } from '@/mock/testPanel';
import type { AccountKind, Beneficiary, BeneficiaryAccount, CountryRef } from '@/types/phase2.types';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import OtpSheet from '@/components/common/auth/OtpSheet';
import MobileTransferFields from './MobileTransferFields';
import LocalTransferFields from './LocalTransferFields';
import InternationalFields from './InternationalFields';
import {
  aliasError,
  blockErrors,
  emptyBlockFor,
  isBlockComplete,
  toNewAccountInput,
  type Block,
} from './formModel';
import { editCardLine, reviewRows } from './beneficiaryDisplay';

const KIND_LABELS: Record<AccountKind, string> = {
  MOBILE_TRANSFER: 'Mobile Transfer',
  LOCAL_TRANSFER: 'Local Transfer',
  INTERNATIONAL: 'International Transfer',
};

interface Entry {
  key: string;
  kind: AccountKind | '';
  block: Block | null;
}

const newEntry = (kind: AccountKind | '' = ''): Entry => ({
  key: `entry_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
  kind,
  block: null,
});

const capitaliseWords = (value: string): string =>
  value.replace(/(^|\s)([a-z])/g, (m) => m.toUpperCase());

interface SaveError {
  message: string;
  fields: string[];
}

/** The server's reason a save or delete failed, with its code when it has one. */
const toSaveError = (err: unknown): SaveError => {
  if (!(err instanceof ApiError)) {
    return { message: 'Something went wrong. Try again.', fields: [] };
  }
  return {
    message: err.code ? `${err.message} (${err.code})` : err.message,
    fields: (err.fieldErrors ?? []).map((f) => `${f.field}: ${f.message}`),
  };
};

const blockHasInput = (block: Block | null): boolean => {
  if (!block) return false;
  const fresh = emptyBlockFor(block.kind);
  return JSON.stringify({ ...block, key: '' }) !== JSON.stringify({ ...fresh, key: '' });
};

const BeneficiaryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mode: 'add' | 'edit' = id ? 'edit' : 'add';
  // Opened from the pay flow (/beneficiaries/add?from=pay): after saving, go
  // straight to the payment for the new payee instead of the manager.
  const fromPay = searchParams.get('from') === 'pay';

  const [loading, setLoading] = useState(mode === 'edit');
  const [loadError, setLoadError] = useState('');
  const [original, setOriginal] = useState<Beneficiary | null>(null);
  const [existingAliases, setExistingAliases] = useState<string[]>([]);
  const [countries, setCountries] = useState<CountryRef[]>([]);

  const [alias, setAlias] = useState('');
  const [aliasTouched, setAliasTouched] = useState(false);
  const [paymentType, setPaymentType] = useState<AccountKind | ''>('');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [keptAccounts, setKeptAccounts] = useState<BeneficiaryAccount[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const [step, setStep] = useState<'form' | 'review'>('form');
  const [showErrors, setShowErrors] = useState(false);
  const [typeChange, setTypeChange] = useState<{ entryKey?: string; kind: AccountKind } | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [deleteLastOpen, setDeleteLastOpen] = useState(false);
  const [otp, setOtp] = useState<{
    authId: string;
    sessionKey: number;
    purpose: 'save' | 'delete';
    destination?: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<SaveError | null>(null);

  useEffect(() => {
    getCountries().then(setCountries).catch(() => setCountries([]));
    listBeneficiaries()
      .then((list) => {
        if (mode === 'edit') {
          const found = list.find((b) => b.id === id);
          if (!found) {
            setLoadError('Beneficiary not found.');
          } else {
            setOriginal(found);
            setAlias(found.alias);
            setKeptAccounts(found.accounts);
            setExistingAliases(list.filter((b) => b.id !== id).map((b) => b.alias));
          }
        } else {
          setExistingAliases(list.map((b) => b.alias));
        }
      })
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Could not load.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const aliasProblem = aliasError(alias, existingAliases);
  const aliasValid = !aliasProblem;

  const allBlocksComplete = entries.every((e) => e.block && isBlockComplete(e.block));
  const remainingSaved = keptAccounts.filter((a) => !removedIds.has(a.id));
  const changed =
    mode === 'add'
      ? alias.trim().length > 0 || entries.some((e) => blockHasInput(e.block))
      : alias.trim() !== (original?.alias ?? '') || removedIds.size > 0 || entries.some((e) => e.block);

  const canSubmit =
    !saving &&
    aliasValid &&
    (mode === 'add'
      ? paymentType !== '' && entries.length > 0 && allBlocksComplete
      : changed && entries.every((e) => !e.kind || (e.block && isBlockComplete(e.block))) &&
        remainingSaved.length + entries.filter((e) => e.block).length > 0 &&
        entries.every((e) => e.block));

  // ---------- entry helpers ----------

  const setEntryKind = (entryKey: string, kind: AccountKind, force = false) => {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.key !== entryKey) return e;
        if (!force && blockHasInput(e.block) && e.kind !== kind) {
          setTypeChange({ entryKey, kind });
          return e;
        }
        return { ...e, kind, block: emptyBlockFor(kind) };
      })
    );
  };

  const choosePaymentType = (kind: AccountKind | '') => {
    if (!kind) return;
    if (entries.some((e) => blockHasInput(e.block)) && kind !== paymentType) {
      setTypeChange({ kind });
      return;
    }
    applyPaymentType(kind);
  };

  const applyPaymentType = (kind: AccountKind) => {
    setPaymentType(kind);
    setEntries([{ ...newEntry(kind), block: emptyBlockFor(kind) }]);
  };

  const confirmTypeChange = () => {
    if (!typeChange) return;
    if (typeChange.entryKey) {
      setEntryKind(typeChange.entryKey, typeChange.kind, true);
    } else {
      applyPaymentType(typeChange.kind);
    }
    setTypeChange(null);
  };

  const updateBlock = (entryKey: string, block: Block) =>
    setEntries((prev) => prev.map((e) => (e.key === entryKey ? { ...e, block } : e)));

  const addAccount = () => {
    if (mode === 'add') {
      if (!paymentType || entries.length >= 5) return;
      setEntries((prev) => [...prev, { ...newEntry(paymentType), block: emptyBlockFor(paymentType) }]);
    } else {
      if (remainingSaved.length + entries.length >= 5) return;
      setEntries((prev) => [...prev, newEntry('')]);
    }
  };

  const removeEntry = (entryKey: string) => setEntries((prev) => prev.filter((e) => e.key !== entryKey));

  const removeSavedAccount = (accountId: string) => {
    const next = new Set(removedIds);
    next.add(accountId);
    const wouldRemain = keptAccounts.filter((a) => !next.has(a.id)).length;
    const newBlocks = entries.filter((e) => e.block).length;
    if (wouldRemain === 0 && newBlocks === 0) {
      // §5.2.5 — removing the only account offers deleting the beneficiary.
      setDeleteLastOpen(true);
      return;
    }
    setRemovedIds(next);
  };

  // ---------- save flow ----------

  const goReview = () => {
    setShowErrors(true);
    if (!canSubmit) return;
    setStep('review');
  };

  const startOtp = async (purpose: 'save' | 'delete') => {
    try {
      const session = await requestBeneficiaryOtp();
      setOtp({
        authId: session.authId,
        sessionKey: Date.now(),
        purpose,
        destination: session.maskedDestination,
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send the OTP.');
    }
  };

  const handleConfirm = () => {
    setSaveError(null);
    void startOtp('save');
  };

  const handleOtpVerify = async (code: string) => {
    if (!otp) return;
    // A wrong or expired code throws here and stays in the OTP sheet.
    await verifyBeneficiaryOtp(otp.authId, code);
    setSaveError(null);
    setSaving(true);
    try {
      if (otp.purpose === 'delete') {
        if (!original) return;
        const { alias: deletedAlias } = await deleteBeneficiary(original.id);
        toast.success(`${deletedAlias} deleted.`);
        navigate('/beneficiaries');
        return;
      }
      const trimmed = alias.trim();
      if (mode === 'add') {
        const created = await createBeneficiary({
          alias: trimmed,
          accounts: entries.map((e) => toNewAccountInput(e.block as Block)),
        });
        // Pay flow: skip the 30-minute cooling (dev) so the new payee can be
        // paid right away, then drop the user into the payment for them.
        if (fromPay && MOCK_BENEFICIARIES) setSkipCooling(true);
        const coolingOn = MOCK_BENEFICIARIES ? !getTestPanelState().skipCooling : true;
        toast.success(
          coolingOn
            ? `${created.alias} added. You can send money after 30 minutes.`
            : `${created.alias} added.`
        );
        if (fromPay) {
          const firstAccount = created.accounts[0];
          navigate(
            `/pay/send?beneficiaryId=${created.id}${firstAccount ? `&accountId=${firstAccount.id}` : ''}`
          );
        } else {
          navigate('/beneficiaries', { state: { openId: created.id } });
        }
      } else if (original) {
        const updated = await updateBeneficiary(original.id, {
          alias: trimmed,
          keepAccountIds: remainingSaved.map((a) => a.id),
          newAccounts: entries.map((e) => toNewAccountInput(e.block as Block)),
        });
        // §17.2 — the toast uses the NEW alias.
        toast.success(`${updated.alias} updated.`);
        navigate('/beneficiaries', { state: { openId: updated.id } });
      }
    } catch (err) {
      // The code was accepted but the server refused the change. Close the
      // sheet and show why here; everything entered stays so it can be fixed.
      setSaveError(toSaveError(err));
    } finally {
      setSaving(false);
      setOtp(null);
    }
  };

  const requestLeave = useCallback(() => {
    if (changed && step === 'form') setDiscardOpen(true);
    else navigate(-1);
  }, [changed, step, navigate]);

  // ---------- render ----------

  if (loading) {
    return (
      <div className="nova-ben-form-page">
        <Loader rows={5} />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="nova-ben-form-page">
        <p className="nova-auth-error">{loadError}</p>
        <Button variant="outline" onClick={() => navigate('/beneficiaries')}>Back</Button>
      </div>
    );
  }

  const removedAccounts = keptAccounts.filter((a) => removedIds.has(a.id));
  const approvalLine =
    mode === 'add' ? `Approve adding ${alias.trim()} as a beneficiary` : `Approve changes to ${alias.trim()}`;

  return (
    <div className="nova-ben-form-page">
      <div className="nova-ben-form-card">
        <div className="nova-ben-form-header">
          <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={requestLeave}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h2>{step === 'review' ? (mode === 'add' ? 'Review beneficiary' : 'Review changes') : mode === 'add' ? 'Add new beneficiary' : 'Edit beneficiary'}</h2>
        </div>

        {saveError && (
          <div className="alert alert-danger" role="alert">
            <div>{saveError.message}</div>
            {saveError.fields.length > 0 && (
              <ul className="mb-0 mt-1">
                {saveError.fields.map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {step === 'form' && (
          <>
            <div className="mb-4">
              <label className="form-label">Beneficiary name (alias)</label>
              <input
                type="text"
                className={`form-control ${aliasTouched && aliasProblem ? 'is-invalid' : ''}`}
                placeholder="Beneficiary name"
                maxLength={40}
                value={alias}
                onChange={(e) => {
                  setAliasTouched(true);
                  setAlias(capitaliseWords(e.target.value));
                }}
              />
              {aliasTouched && aliasProblem && <div className="invalid-feedback d-block">{aliasProblem}</div>}
            </div>

            {mode === 'edit' && (
              <div className="mb-4">
                {remainingSaved.length === 0 && entries.length === 0 && (
                  <p className="nova-info-box">No accounts. Add one below.</p>
                )}
                {remainingSaved.map((account) => (
                  <div className="nova-saved-account" key={account.id}>
                    <span>{editCardLine(account, countries)}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeSavedAccount(account.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="nova-account-details">
              <h3 className="nova-account-details-title">Account Details</h3>

              {mode === 'add' && (
                <div className="mb-3">
                  <label className="form-label">Payment type</label>
                  <select
                    className="form-select"
                    value={paymentType}
                    onChange={(e) => choosePaymentType(e.target.value as AccountKind | '')}
                  >
                    <option value="">-- Select payment type --</option>
                    {Object.entries(KIND_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {entries.map((entry, index) => (
                <div className="nova-block" key={entry.key}>
                  <div className="nova-block-header">
                    <h4>
                      {mode === 'add' ? `Beneficiary account ${index + 1}` : `New account ${index + 1}`}
                    </h4>
                    {(mode === 'edit' || index > 0) && (
                      <button
                        type="button"
                        className="nova-icon-btn nova-icon-btn-danger"
                        title="Delete"
                        aria-label="Delete account block"
                        onClick={() => removeEntry(entry.key)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>

                  {mode === 'edit' && (
                    <div className="mb-3">
                      <label className="form-label">Payment type</label>
                      <select
                        className="form-select"
                        value={entry.kind}
                        onChange={(e) => setEntryKind(entry.key, e.target.value as AccountKind)}
                      >
                        <option value="">-- Select payment type --</option>
                        {Object.entries(KIND_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {entry.block?.kind === 'MOBILE_TRANSFER' && (
                    <MobileTransferFields
                      block={entry.block}
                      showErrors={showErrors}
                      onChange={(b) => updateBlock(entry.key, b)}
                    />
                  )}
                  {entry.block?.kind === 'LOCAL_TRANSFER' && (
                    <LocalTransferFields
                      block={entry.block}
                      errors={blockErrors(entry.block)}
                      showErrors={showErrors}
                      onChange={(b) => updateBlock(entry.key, b)}
                    />
                  )}
                  {entry.block?.kind === 'INTERNATIONAL' && (
                    <InternationalFields
                      block={entry.block}
                      errors={blockErrors(entry.block)}
                      showErrors={showErrors}
                      countries={countries}
                      onChange={(b) => updateBlock(entry.key, b)}
                    />
                  )}
                </div>
              ))}

              {(mode === 'add' ? paymentType !== '' && entries.length < 5 : remainingSaved.length + entries.length < 5) && (
                <button type="button" className="btn btn-outline-primary w-100" onClick={addAccount}>
                  <FontAwesomeIcon icon={faPlus} /> Add Account
                </button>
              )}
            </div>

            <div className="nova-form-footer">
              <Button variant="outline" onClick={requestLeave}>
                Cancel
              </Button>
              <Button onClick={goReview} disabled={!canSubmit && showErrors}>
                {mode === 'add' ? 'Add' : 'Save'}
              </Button>
            </div>
          </>
        )}

        {step === 'review' && (
          <>
            <dl className="nova-review-rows">
              <div className="nova-ben-row">
                <dt>Beneficiary name</dt>
                <dd>{alias.trim()}</dd>
              </div>
              {mode === 'edit' && original && alias.trim() !== original.alias && (
                <div className="nova-ben-row">
                  <dt>Was</dt>
                  <dd>{original.alias}</dd>
                </div>
              )}
            </dl>

            {mode === 'edit' && remainingSaved.length > 0 && (
              <div className="mb-3">
                {remainingSaved.map((account) => (
                  <div className="nova-saved-account" key={account.id}>
                    <span>{editCardLine(account, countries)}</span>
                  </div>
                ))}
              </div>
            )}
            {mode === 'edit' && removedAccounts.length > 0 && (
              <div className="mb-3">
                {removedAccounts.map((account) => (
                  <div className="nova-saved-account nova-removing" key={account.id}>
                    <span>Removing · {editCardLine(account, countries)}</span>
                  </div>
                ))}
              </div>
            )}

            {entries.map((entry, index) =>
              entry.block ? (
                <div className="nova-block" key={entry.key}>
                  <div className="nova-block-header">
                    <h4>{mode === 'add' ? `Beneficiary account ${index + 1}` : `New account ${index + 1}`}</h4>
                  </div>
                  <dl className="nova-review-rows">
                    {reviewRows(toNewAccountInput(entry.block), countries).map((row, i) => (
                      <div className="nova-ben-row" key={i}>
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null
            )}

            <div className="nova-form-footer">
              <Button variant="outline" onClick={() => setStep('form')}>
                Back
              </Button>
              <Button onClick={handleConfirm} isLoading={saving}>
                Confirm
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Change payment type (§5.1) */}
      <Modal show={Boolean(typeChange)} onHide={() => setTypeChange(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change payment type?</Modal.Title>
        </Modal.Header>
        <Modal.Body>The accounts you&apos;ve entered will be cleared.</Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setTypeChange(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={confirmTypeChange}>
            Change
          </button>
        </Modal.Footer>
      </Modal>

      {/* Discard unsaved input (§5.3, §17.7) */}
      <Modal show={discardOpen} onHide={() => setDiscardOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Discard entered data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mode === 'add'
            ? 'Are you sure to exit creation of a new contact? The data you have entered will be lost.'
            : 'Your changes will be lost.'}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setDiscardOpen(false)}>
            Continue
          </button>
          <button type="button" className="btn btn-danger" onClick={() => navigate(-1)}>
            Discard
          </button>
        </Modal.Footer>
      </Modal>

      {/* Removing the only account (§5.2.5) */}
      <Modal show={deleteLastOpen} onHide={() => setDeleteLastOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>This is {original?.alias}&apos;s only account. Delete the beneficiary?</Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setDeleteLastOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              setDeleteLastOpen(false);
              void startOtp('delete');
            }}
          >
            Delete
          </button>
        </Modal.Footer>
      </Modal>

      <OtpSheet
        show={Boolean(otp)}
        approvalLine={otp?.purpose === 'save' ? approvalLine : undefined}
        destination={otp?.destination}
        sessionKey={otp?.sessionKey}
        onVerify={handleOtpVerify}
        onResend={async () => {
          if (!otp) return 180;
          const { expiresInSeconds } = await resendBeneficiaryOtp(otp.authId);
          return expiresInSeconds;
        }}
        onClose={() => setOtp(null)}
      />
    </div>
  );
};

export default BeneficiaryForm;
